/**
 * Camunda 7.18 Process Definition REST API Service
 * https://docs.camunda.org/manual/7.18/reference/rest/process-definition/
 */

import { CamundaApiClient, getCamundaApiClient } from '../CamundaApiClient';
import type {
  ProcessDefinition,
  ProcessDefinitionQuery,
  ProcessDefinitionStatistics,
  CountResult,
  StartProcessInstanceDto,
  ProcessInstance,
  FormInfo,
  Variables,
  CalledProcessDefinition,
} from '../types';

export class ProcessDefinitionService {
  private client: CamundaApiClient;
  private basePath = '/engine-rest/process-definition';

  constructor(client?: CamundaApiClient) {
    this.client = client || getCamundaApiClient();
  }

  // ============================================================================
  // Get List
  // ============================================================================

  /**
   * Get a list of process definitions
   */
  async getList(query?: ProcessDefinitionQuery): Promise<ProcessDefinition[]> {
    const params = this.buildQueryParams(query);
    return this.client.get<ProcessDefinition[]>(`${this.basePath}${params}`);
  }

  /**
   * Get process definitions count
   */
  async getCount(query?: ProcessDefinitionQuery): Promise<CountResult> {
    const params = this.buildQueryParams(query);
    return this.client.get<CountResult>(`${this.basePath}/count${params}`);
  }

  /**
   * Get process definitions using POST (for complex queries)
   */
  async queryList(query: ProcessDefinitionQuery): Promise<ProcessDefinition[]> {
    const { firstResult, maxResults, sortBy, sortOrder, ...body } = query;
    const params = this.buildPaginationParams({ firstResult, maxResults, sortBy, sortOrder });
    return this.client.post<ProcessDefinition[]>(`${this.basePath}${params}`, body);
  }

  /**
   * Get process definitions count using POST
   */
  async queryCount(query: ProcessDefinitionQuery): Promise<CountResult> {
    return this.client.post<CountResult>(`${this.basePath}/count`, query);
  }

  // ============================================================================
  // Get Single
  // ============================================================================

  /**
   * Get process definition by ID
   */
  async getById(id: string): Promise<ProcessDefinition> {
    return this.client.get<ProcessDefinition>(`${this.basePath}/${id}`);
  }

  /**
   * Get process definition by key (latest version)
   */
  async getByKey(key: string): Promise<ProcessDefinition> {
    return this.client.get<ProcessDefinition>(`${this.basePath}/key/${key}`);
  }

  /**
   * Get process definition by key and tenant ID
   */
  async getByKeyAndTenant(key: string, tenantId: string): Promise<ProcessDefinition> {
    return this.client.get<ProcessDefinition>(`${this.basePath}/key/${key}/tenant-id/${tenantId}`);
  }

  // ============================================================================
  // BPMN XML
  // ============================================================================

  /**
   * Get BPMN 2.0 XML by process definition ID
   */
  async getXmlById(id: string): Promise<{ id: string; bpmn20Xml: string }> {
    return this.client.get<{ id: string; bpmn20Xml: string }>(`${this.basePath}/${id}/xml`);
  }

  /**
   * Get BPMN 2.0 XML by key
   */
  async getXmlByKey(key: string): Promise<{ id: string; bpmn20Xml: string }> {
    return this.client.get<{ id: string; bpmn20Xml: string }>(`${this.basePath}/key/${key}/xml`);
  }

  /**
   * Get BPMN 2.0 XML by key and tenant
   */
  async getXmlByKeyAndTenant(key: string, tenantId: string): Promise<{ id: string; bpmn20Xml: string }> {
    return this.client.get<{ id: string; bpmn20Xml: string }>(
      `${this.basePath}/key/${key}/tenant-id/${tenantId}/xml`
    );
  }

  // ============================================================================
  // Diagram
  // ============================================================================

  /**
   * Get process diagram by ID
   */
  async getDiagramById(id: string): Promise<Blob> {
    return this.client.getBlob(`${this.basePath}/${id}/diagram`);
  }

  /**
   * Get process diagram by key
   */
  async getDiagramByKey(key: string): Promise<Blob> {
    return this.client.getBlob(`${this.basePath}/key/${key}/diagram`);
  }

  // ============================================================================
  // Start Process Instance
  // ============================================================================

  /**
   * Start a process instance by definition ID
   */
  async startById(id: string, dto?: StartProcessInstanceDto): Promise<ProcessInstance> {
    return this.client.post<ProcessInstance>(`${this.basePath}/${id}/start`, dto || {});
  }

  /**
   * Start a process instance by key
   */
  async startByKey(key: string, dto?: StartProcessInstanceDto): Promise<ProcessInstance> {
    return this.client.post<ProcessInstance>(`${this.basePath}/key/${key}/start`, dto || {});
  }

  /**
   * Start a process instance by key and tenant
   */
  async startByKeyAndTenant(
    key: string,
    tenantId: string,
    dto?: StartProcessInstanceDto
  ): Promise<ProcessInstance> {
    return this.client.post<ProcessInstance>(
      `${this.basePath}/key/${key}/tenant-id/${tenantId}/start`,
      dto || {}
    );
  }

  // ============================================================================
  // Form
  // ============================================================================

  /**
   * Get start form key by ID
   */
  async getStartFormById(id: string): Promise<FormInfo> {
    return this.client.get<FormInfo>(`${this.basePath}/${id}/startForm`);
  }

  /**
   * Get start form key by key
   */
  async getStartFormByKey(key: string): Promise<FormInfo> {
    return this.client.get<FormInfo>(`${this.basePath}/key/${key}/startForm`);
  }

  /**
   * Get rendered start form by ID
   */
  async getRenderedStartFormById(id: string): Promise<string> {
    return this.client.getText(`${this.basePath}/${id}/rendered-form`);
  }

  /**
   * Get rendered start form by key
   */
  async getRenderedStartFormByKey(key: string): Promise<string> {
    return this.client.getText(`${this.basePath}/key/${key}/rendered-form`);
  }

  /**
   * Get deployed start form by ID
   */
  async getDeployedStartFormById(id: string): Promise<string> {
    return this.client.getText(`${this.basePath}/${id}/deployed-start-form`);
  }

  /**
   * Submit start form by ID
   */
  async submitStartFormById(id: string, variables?: Variables): Promise<ProcessInstance> {
    return this.client.post<ProcessInstance>(`${this.basePath}/${id}/submit-form`, { variables });
  }

  /**
   * Submit start form by key
   */
  async submitStartFormByKey(key: string, variables?: Variables): Promise<ProcessInstance> {
    return this.client.post<ProcessInstance>(`${this.basePath}/key/${key}/submit-form`, { variables });
  }

  // ============================================================================
  // Form Variables
  // ============================================================================

  /**
   * Get start form variables by ID
   */
  async getStartFormVariablesById(
    id: string,
    variableNames?: string[],
    deserializeValues?: boolean
  ): Promise<Variables> {
    let params = '';
    if (variableNames?.length) {
      params += `?variableNames=${variableNames.join(',')}`;
    }
    if (deserializeValues !== undefined) {
      params += `${params ? '&' : '?'}deserializeValues=${deserializeValues}`;
    }
    return this.client.get<Variables>(`${this.basePath}/${id}/form-variables${params}`);
  }

  /**
   * Get start form variables by key
   */
  async getStartFormVariablesByKey(
    key: string,
    variableNames?: string[],
    deserializeValues?: boolean
  ): Promise<Variables> {
    let params = '';
    if (variableNames?.length) {
      params += `?variableNames=${variableNames.join(',')}`;
    }
    if (deserializeValues !== undefined) {
      params += `${params ? '&' : '?'}deserializeValues=${deserializeValues}`;
    }
    return this.client.get<Variables>(`${this.basePath}/key/${key}/form-variables${params}`);
  }

  // ============================================================================
  // Statistics
  // ============================================================================

  /**
   * Get process definition statistics
   */
  async getStatistics(options?: {
    failedJobs?: boolean;
    incidents?: boolean;
    incidentsForType?: string;
    rootIncidents?: boolean;
  }): Promise<ProcessDefinitionStatistics[]> {
    const params = new URLSearchParams();
    if (options?.failedJobs) params.append('failedJobs', 'true');
    if (options?.incidents) params.append('incidents', 'true');
    if (options?.incidentsForType) params.append('incidentsForType', options.incidentsForType);
    if (options?.rootIncidents) params.append('rootIncidents', 'true');
    const queryString = params.toString();
    return this.client.get<ProcessDefinitionStatistics[]>(
      `${this.basePath}/statistics${queryString ? `?${queryString}` : ''}`
    );
  }

  /**
   * Get activity statistics by process definition ID
   */
  async getActivityStatisticsById(
    id: string,
    options?: {
      failedJobs?: boolean;
      incidents?: boolean;
      incidentsForType?: string;
    }
  ): Promise<any[]> {
    const params = new URLSearchParams();
    if (options?.failedJobs) params.append('failedJobs', 'true');
    if (options?.incidents) params.append('incidents', 'true');
    if (options?.incidentsForType) params.append('incidentsForType', options.incidentsForType);
    const queryString = params.toString();
    return this.client.get<any[]>(
      `${this.basePath}/${id}/statistics${queryString ? `?${queryString}` : ''}`
    );
  }

  // ============================================================================
  // Suspension
  // ============================================================================

  /**
   * Activate/Suspend process definition by ID
   */
  async updateSuspensionStateById(
    id: string,
    suspended: boolean,
    options?: {
      includeProcessInstances?: boolean;
      executionDate?: string;
    }
  ): Promise<void> {
    await this.client.put<void>(`${this.basePath}/${id}/suspended`, {
      suspended,
      ...options,
    });
  }

  /**
   * Activate/Suspend process definition by key
   */
  async updateSuspensionStateByKey(
    key: string,
    suspended: boolean,
    options?: {
      includeProcessInstances?: boolean;
      executionDate?: string;
      processDefinitionTenantId?: string;
      processDefinitionWithoutTenantId?: boolean;
    }
  ): Promise<void> {
    await this.client.put<void>(`${this.basePath}/key/${key}/suspended`, {
      suspended,
      ...options,
    });
  }

  /**
   * Activate/Suspend by process definition ID, key, or tenant
   */
  async updateSuspensionState(dto: {
    processDefinitionId?: string;
    processDefinitionKey?: string;
    suspended: boolean;
    includeProcessInstances?: boolean;
    executionDate?: string;
    processDefinitionTenantId?: string;
    processDefinitionWithoutTenantId?: boolean;
  }): Promise<void> {
    await this.client.put<void>(`${this.basePath}/suspended`, dto);
  }

  // ============================================================================
  // History Time to Live
  // ============================================================================

  /**
   * Update history time to live by ID
   */
  async updateHistoryTimeToLiveById(id: string, historyTimeToLive: number | null): Promise<void> {
    await this.client.put<void>(`${this.basePath}/${id}/history-time-to-live`, {
      historyTimeToLive,
    });
  }

  /**
   * Update history time to live by key
   */
  async updateHistoryTimeToLiveByKey(key: string, historyTimeToLive: number | null): Promise<void> {
    await this.client.put<void>(`${this.basePath}/key/${key}/history-time-to-live`, {
      historyTimeToLive,
    });
  }

  // ============================================================================
  // Called Process Definitions
  // ============================================================================

  /**
   * Get called process definitions (static)
   */
  async getStaticCalledProcessDefinitions(id: string): Promise<CalledProcessDefinition[]> {
    return this.client.get<CalledProcessDefinition[]>(
      `${this.basePath}/${id}/static-called-process-definitions`
    );
  }

  // ============================================================================
  // Delete
  // ============================================================================

  /**
   * Delete process definition by ID
   */
  async deleteById(
    id: string,
    options?: {
      cascade?: boolean;
      skipCustomListeners?: boolean;
      skipIoMappings?: boolean;
    }
  ): Promise<void> {
    const params = new URLSearchParams();
    if (options?.cascade) params.append('cascade', 'true');
    if (options?.skipCustomListeners) params.append('skipCustomListeners', 'true');
    if (options?.skipIoMappings) params.append('skipIoMappings', 'true');
    const queryString = params.toString();
    await this.client.delete<void>(`${this.basePath}/${id}${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Delete process definition by key
   */
  async deleteByKey(
    key: string,
    options?: {
      cascade?: boolean;
      skipCustomListeners?: boolean;
      skipIoMappings?: boolean;
    }
  ): Promise<void> {
    const params = new URLSearchParams();
    if (options?.cascade) params.append('cascade', 'true');
    if (options?.skipCustomListeners) params.append('skipCustomListeners', 'true');
    if (options?.skipIoMappings) params.append('skipIoMappings', 'true');
    const queryString = params.toString();
    await this.client.delete<void>(`${this.basePath}/key/${key}/delete${queryString ? `?${queryString}` : ''}`);
  }

  // ============================================================================
  // Restart Process Instance
  // ============================================================================

  /**
   * Restart process instances by process definition ID
   */
  async restartProcessInstance(
    id: string,
    dto: {
      processInstanceIds?: string[];
      historicProcessInstanceQuery?: any;
      skipCustomListeners?: boolean;
      skipIoMappings?: boolean;
      initialVariables?: boolean;
      withoutBusinessKey?: boolean;
      instructions: any[];
    }
  ): Promise<void> {
    await this.client.post<void>(`${this.basePath}/${id}/restart`, dto);
  }

  /**
   * Restart process instances async
   */
  async restartProcessInstanceAsync(
    id: string,
    dto: {
      processInstanceIds?: string[];
      historicProcessInstanceQuery?: any;
      skipCustomListeners?: boolean;
      skipIoMappings?: boolean;
      initialVariables?: boolean;
      withoutBusinessKey?: boolean;
      instructions: any[];
    }
  ): Promise<{ id: string }> {
    return this.client.post<{ id: string }>(`${this.basePath}/${id}/restart-async`, dto);
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  private buildQueryParams(query?: ProcessDefinitionQuery): string {
    if (!query) return '';
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          params.append(key, value.join(','));
        } else {
          params.append(key, String(value));
        }
      }
    });
    const queryString = params.toString();
    return queryString ? `?${queryString}` : '';
  }

  private buildPaginationParams(params: {
    firstResult?: number;
    maxResults?: number;
    sortBy?: string;
    sortOrder?: string;
  }): string {
    const urlParams = new URLSearchParams();
    if (params.firstResult !== undefined) urlParams.append('firstResult', String(params.firstResult));
    if (params.maxResults !== undefined) urlParams.append('maxResults', String(params.maxResults));
    if (params.sortBy) urlParams.append('sortBy', params.sortBy);
    if (params.sortOrder) urlParams.append('sortOrder', params.sortOrder);
    const queryString = urlParams.toString();
    return queryString ? `?${queryString}` : '';
  }
}

// Singleton instance
let processDefinitionServiceInstance: ProcessDefinitionService | null = null;

export function getProcessDefinitionService(): ProcessDefinitionService {
  if (!processDefinitionServiceInstance) {
    processDefinitionServiceInstance = new ProcessDefinitionService();
  }
  return processDefinitionServiceInstance;
}

export default ProcessDefinitionService;
